import React, { createContext, useContext, useState, ReactNode } from 'react';

interface FriedhofContextValue {
  /** Currently selected Friedhof (null until the user picks one). */
  friedhofId: number | null;
  friedhofName: string | null;
  /**
   * Whether the user has actively chosen a Friedhof for this session.
   * Session-scoped and reset on every login, so a fresh login always shows
   * the selection screen (while the last choice is remembered as the default).
   */
  isFriedhofSelected: boolean;
  selectFriedhof: (id: number, name: string) => void;
  /** Forget the confirmation (login / "Friedhof wechseln") -> selection screen. */
  resetFriedhofSelection: () => void;
  /**
   * Wie viele Friedhöfe es gibt (null = noch nicht geladen). Bei nur einem gibt es nichts zu
   * wechseln: kein „Friedhof wechseln“, keine Auswahlseite. Wer die Liste lädt, meldet die Zahl.
   */
  anzahlFriedhoefe: number | null;
  setAnzahlFriedhoefe: (anzahl: number) => void;
}

const FriedhofContext = createContext<FriedhofContextValue | undefined>(undefined);

const ID_KEY = 'tfp.friedhofId';
const NAME_KEY = 'tfp.friedhofName';
const SELECTED_KEY = 'tfp.friedhofSelected';

export const FriedhofProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [friedhofId, setFriedhofId] = useState<number | null>(() => {
    const stored = localStorage.getItem(ID_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [friedhofName, setFriedhofName] = useState<string | null>(() => localStorage.getItem(NAME_KEY));
  const [anzahlFriedhoefe, setAnzahlFriedhoefe] = useState<number | null>(null);
  const [isFriedhofSelected, setIsFriedhofSelected] = useState<boolean>(
    () => sessionStorage.getItem(SELECTED_KEY) === 'true'
  );

  const selectFriedhof = (id: number, name: string) => {
    setFriedhofId(id);
    setFriedhofName(name);
    localStorage.setItem(ID_KEY, String(id));
    localStorage.setItem(NAME_KEY, name);
    setIsFriedhofSelected(true);
    sessionStorage.setItem(SELECTED_KEY, 'true');
  };

  const resetFriedhofSelection = () => {
    setIsFriedhofSelected(false);
    sessionStorage.removeItem(SELECTED_KEY);
  };

  return (
    <FriedhofContext.Provider
      value={{
        friedhofId, friedhofName, isFriedhofSelected, selectFriedhof, resetFriedhofSelection,
        anzahlFriedhoefe, setAnzahlFriedhoefe,
      }}
    >
      {children}
    </FriedhofContext.Provider>
  );
};

export const useFriedhof = (): FriedhofContextValue => {
  const ctx = useContext(FriedhofContext);
  if (!ctx) throw new Error('useFriedhof must be used within a FriedhofProvider');
  return ctx;
};
